import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './tabs.css';

import { faClass } from '@/constants/icons';

/**
 * Contenitore per tabs.
 * Props:
 * - children: <Tab ...>...</Tab>
 * - defaultActiveKey: string (non controllato)
 * - activeKey: string (controllato)
 * - onChange: (nextKey) => void
 * - keepMounted: bool (mantiene montati i pannelli non attivi; default: false)
 * - className: string (wrapper extra class)
 * - variant: "tabs" | "pills" (default: "tabs") -> cambia classi nav
 * - contentClassName: string (classi extra per il contenitore dei pannelli)
 */
const TabsContainer = ({
  children,
  defaultActiveKey,
  activeKey,
  onChange,
  keepMounted = false,
  className = '',
  variant = 'tabs',
  contentClassName = 'p-3 border border-top-0 rounded-bottom',
}) => {
  const uid = useId();

  const tabs = useMemo(() => {
    return React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((el, idx) => {
        const { eventKey, label, icon, badge, disabled } = el.props || {};
        const key = eventKey ?? String(idx);
        return {
          key,
          label,
          icon,
          badge,
          disabled: !!disabled,
          content: el.props?.children,
        };
      });
  }, [children]);

  // Trova prima tab abilitata
  const firstEnabledKey = useMemo(() => {
    return tabs.find((t) => !t.disabled)?.key ?? tabs[0]?.key ?? null;
  }, [tabs]);

  // Stato non controllato
  const [internalKey, setInternalKey] = useState(() => {
    return defaultActiveKey ?? firstEnabledKey;
  });

  // Aggiorna stato iniziale se tabs cambiano
  useEffect(() => {
    if (internalKey == null && firstEnabledKey != null) {
      setInternalKey(firstEnabledKey);
    }
  }, [firstEnabledKey, internalKey]);

  const isControlled = typeof activeKey !== 'undefined' && activeKey !== null;
  const currentKey = isControlled ? activeKey : internalKey;

  const headerRefs = useRef({}); // mappa key -> ref button
  const setHeaderRef = (key) => (el) => {
    if (el) headerRefs.current[key] = el;
  };

  const switchTo = (nextKey) => {
    if (tabs.find((t) => t.key === nextKey && !t.disabled)) {
      if (!isControlled) setInternalKey(nextKey);
      onChange?.(nextKey);
      // sposta focus al bottone tab
      requestAnimationFrame(() => headerRefs.current[nextKey]?.focus?.());
    }
  };

  const moveFocus = (dir) => {
    const enabled = tabs.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const idx = enabled.findIndex((t) => t.key === currentKey);
    const next = (idx + dir + enabled.length) % enabled.length;
    switchTo(enabled[next].key);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveFocus(+1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveFocus(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      switchTo(tabs.find((t) => !t.disabled)?.key);
    } else if (e.key === 'End') {
      e.preventDefault();
      switchTo([...tabs].reverse().find((t) => !t.disabled)?.key);
    }
  };

  const navClass = variant === 'pills' ? 'nav nav-pills' : 'nav nav-tabs';

  // ...dentro TabsContainer.jsx

  return (
    <div className={`tabs-equal ${className || ''}`}>
      {/* Header: distribuzione uniforme della larghezza */}
      <ul
        className={navClass}
        role="tablist"
        onKeyDown={onKeyDown}
        style={{ display: 'flex', gap: 0 }} // divide 100% per N tab
      >
        {tabs.map((t) => {
          const isActive = t.key === currentKey;
          return (
            <li
              key={t.key}
              className="nav-item"
              role="presentation"
              style={{ flex: '1 1 0' }} // ogni tab prende 1/N della riga
            >
              <button
                type="button"
                role="tab"
                id={`tab-${uid}-${t.key}`}
                aria-controls={`panel-${uid}-${t.key}`}
                aria-selected={isActive}
                className={`nav-link ${isActive ? 'active' : ''} ${t.disabled ? 'disabled' : ''}`}
                disabled={t.disabled}
                onClick={() => switchTo(t.key)}
                ref={setHeaderRef(t.key)}
                // Icona, etichetta e badge stanno **insieme**, centrati nella
                // tab. Prima erano tre colonne di griglia (`auto 1fr auto`) e la
                // colonna elastica in mezzo separava l'icona dalla sua etichetta:
                // con due sole tab larghe mezza pagina, l'icona finiva all'estrema
                // sinistra e il testo al centro, come se non si appartenessero.
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
                title={typeof t.label === 'string' ? t.label : undefined} // tooltip con testo completo
              >
                {t.icon && <i className={faClass(t.icon)} aria-hidden="true" />}

                <span
                  className="tab-label"
                  style={{
                    minWidth: 0, // fondamentale per ellissi in layout grid/flex
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label ?? t.key}
                </span>

                {typeof t.badge !== 'undefined' && (
                  <span className="badge bg-secondary">{t.badge}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Panels */}
      <div className={`tab-content ${contentClassName}`}>
        {tabs.map((t) => {
          const isActive = t.key === currentKey;
          if (!keepMounted && !isActive) return null;
          return (
            <div
              key={t.key}
              role="tabpanel"
              id={`panel-${uid}-${t.key}`}
              aria-labelledby={`tab-${uid}-${t.key}`}
              className={`tab-pane fade ${isActive ? 'show active' : ''} ${!isActive && keepMounted ? 'd-none' : ''}`}
            >
              {t.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TabsContainer;
